import { ActionReducerMap, createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromTeams from './teams/teams.reducer';
import * as fromUsers from './users/users.reducer';
import * as fromStrategicImperatives from './strategic-imperatives/strategic-imperatives.reducer';
import * as fromTeamTypesReducer from './team-types/team-types.reducer'; // Renamed import
import * as fromFinanceThresholds from './finance-thresholds/finance-thresholds.reducer';
import { StrategicImperativesEffects } from './strategic-imperatives/strategic-imperatives.effects';
import { TeamsEffects } from './teams/teams.effects';
import { UsersEffects } from './users/users.effects';
import { TeamTypeEffects } from './team-types/team-types.effects';
import { FinanceThresholdEffects } from './finance-thresholds/finance-thresholds.effects';
import * as fromTeamTypesSelectors from './team-types/team-types.selectors'; // Import selectors

export interface AdminState {
  teams: fromTeams.TeamsState;
  users: fromUsers.UsersState;
  strategicImperatives: fromStrategicImperatives.StrategicImperativesState;
  teamTypes: fromTeamTypesReducer.TeamTypesState;
  financeThresholds: fromFinanceThresholds.FinanceThresholdState;
}

export const adminReducers: ActionReducerMap<AdminState> = {
  teams: fromTeams.teamsReducer,
  users: fromUsers.usersReducer,
  strategicImperatives: fromStrategicImperatives.strategicImperativesReducer,
  teamTypes: fromTeamTypesReducer.teamTypeReducer,
  financeThresholds: fromFinanceThresholds.financeThresholdReducer,
};

export const adminEffects = [StrategicImperativesEffects, TeamsEffects, UsersEffects, TeamTypeEffects, FinanceThresholdEffects];

// Feature Selector for Admin
export const selectAdminState = createFeatureSelector<AdminState>('admin');

// Selectors for Strategic Imperatives
export const selectStrategicImperativesState = createSelector(selectAdminState, (state: AdminState | undefined) => state?.strategicImperatives);

export const selectAllStrategicImperatives = createSelector(selectStrategicImperativesState, fromStrategicImperatives.selectAllStrategicImperatives);

export const selectStrategicImperativesLoading = createSelector(selectStrategicImperativesState, fromStrategicImperatives.selectStrategicImperativesLoading);

export const selectStrategicImperativesError = createSelector(selectStrategicImperativesState, fromStrategicImperatives.selectStrategicImperativesError);

// Teams selectors under admin feature
export const selectTeamsState = createSelector(selectAdminState, (state: AdminState) => state.teams);

export const selectAllTeams = createSelector(
  selectTeamsState,
  (state: fromTeams.TeamsState) => state.teams // Directly access the 'teams' array
);

export const selectTeamsLoading = createSelector(
  selectTeamsState,
  (state: fromTeams.TeamsState) => state.loading // Directly access 'loading'
);

export const selectTeamsError = createSelector(
  selectTeamsState,
  (state: fromTeams.TeamsState) => state.error // Directly access 'error'
);

export const selectTotalTeams = createSelector(
  selectTeamsState,
  (state: fromTeams.TeamsState) => state.totalRecords // Directly access 'totalRecords'
);

export const selectSelectedTeam = createSelector(
  selectTeamsState,
  (state: fromTeams.TeamsState) => state.selectedTeam // Directly access 'selectedTeam'
);

export const selectCreateSuccess = createSelector(
  selectTeamsState,
  (state: fromTeams.TeamsState) => state.createSuccess // Directly access 'createSuccess'
);

// Parameterized selectors
export const getTeamById = (id: string) => createSelector(selectTeamsState, fromTeams.getTeamById(id));

export const getTeamMembers = (teamId: string) => createSelector(selectTeamsState, fromTeams.getTeamMembers(teamId));

export const getFinalReviewers = (teamId: string) => createSelector(selectTeamsState, fromTeams.getFinalReviewers(teamId));

export const getFinanceReviewers = (teamId: string) => createSelector(selectTeamsState, fromTeams.getFinanceReviewers(teamId));

export const getGembaLeads = (teamId: string) => createSelector(selectTeamsState, fromTeams.getGembaLeads(teamId));

export const getWorkspaceLeads = (teamId: string) => createSelector(selectTeamsState, fromTeams.getWorkspaceLeads(teamId));

// Selectors for Team Types
export const selectTeamTypesState = createSelector(selectAdminState, (state: AdminState | undefined) => state?.teamTypes);

// Use imported selectors
export const selectAllTeamTypes = fromTeamTypesSelectors.selectAllTeamTypes;
export const selectTeamTypesLoading = fromTeamTypesSelectors.selectTeamTypesLoading;
export const selectTeamTypesError = fromTeamTypesSelectors.selectTeamTypesError;
export const selectCreateTeamTypeSuccess = fromTeamTypesSelectors.selectCreateTeamTypeSuccess;
export const selectUpdateTeamTypeSuccess = fromTeamTypesSelectors.selectUpdateTeamTypeSuccess;
export const selectTeamTypeById = fromTeamTypesSelectors.selectTeamTypeById;
// Add any additional selectors for Team Types as needed

// Selectors for Finance Thresholds
export const selectFinanceThresholdsState = createSelector(selectAdminState, (state: AdminState | undefined) => state?.financeThresholds);

export const selectAllFinanceThresholds = createSelector(selectFinanceThresholdsState, (state: fromFinanceThresholds.FinanceThresholdState | undefined) => state?.thresholds || []);

export const selectFinanceThresholdsLoading = createSelector(selectFinanceThresholdsState, (state: fromFinanceThresholds.FinanceThresholdState | undefined) => state?.loading || false);

export const selectFinanceThresholdsError = createSelector(selectFinanceThresholdsState, (state: fromFinanceThresholds.FinanceThresholdState | undefined) => state?.error);

// Selectors for Users
export const selectUsersState = createSelector(selectAdminState, (state: AdminState) => state.users);

export const selectAllUsers = createSelector(
  selectUsersState,
  (state: fromUsers.UsersState) => state.users
);

export const selectUsersLoading = createSelector(
  selectUsersState,
  (state: fromUsers.UsersState) => state.loading
);

export const selectUsersError = createSelector(
  selectUsersState,
  (state: fromUsers.UsersState) => state.error
);

export const selectTotalUsers = createSelector(
  selectUsersState,
  (state: fromUsers.UsersState) => state.totalRecords
);

export const selectSelectedUser = createSelector(
  selectUsersState,
  (state: fromUsers.UsersState) => state.selectedUser
);

export const selectCurrentUser = createSelector(
  selectUsersState,
  (state: fromUsers.UsersState) => state.currentUser
);

export const selectCreateUserSuccess = createSelector(
  selectUsersState,
  (state: fromUsers.UsersState) => state.createSuccess
);
